import { useCallback, useEffect } from "react";
import { atom, useAtom } from "jotai";
import dataService from "../../../helpers/dataService";
import { readPersisted, writePersisted } from "../../Hooks/usePersistedState/usePersistedState";
import { computeNbPages, countActiveFilters } from "./listHelpers";

// ============================================================
// useListSearch — état et actions d'une liste paginée côté serveur.
// - L'état vit dans deux atomes Jotai fournis par la page (persistance
//   entre les navigations, partage entre composants de la même liste).
// - Le fetcher est injectable (tests sans réseau).
// - Les réponses obsolètes (requête plus ancienne arrivant après une plus
//   récente) sont ignorées.
// - `persistKey` (optionnel) : la dernière recherche envoyée est mémorisée
//   dans localStorage et relue à la création des atomes (retour sur l'écran
//   après un rechargement). Seules les clés connues des filtres par défaut
//   sont reprises : un filtre disparu ne revient pas hanter la requête.
// ============================================================

export const INITIAL_RESULTS = {
  items: [],
  count: 0,
  status: "idle", // idle | loading | success | error
  error: null,
  hasSearched: false,
  appliedFilters: null,
};

const positive = (value) => (Number(value) > 0 ? Number(value) : 0);

// Recherche mémorisée sous `persistKey`, rabattue sur les filtres connus.
export function readPersistedSearch(persistKey, defaultFilters = {}) {
  const memo = persistKey ? readPersisted(persistKey, null) : null;
  if (!memo || typeof memo !== "object") return null;
  const filters = Object.fromEntries(
    Object.keys(defaultFilters).map((name) => [
      name,
      memo.filters && memo.filters[name] !== undefined && memo.filters[name] !== null
        ? memo.filters[name]
        : defaultFilters[name],
    ])
  );
  return { filters, page: positive(memo.page), nbItemsByPage: positive(memo.nbItemsByPage) };
}

export function createListAtoms({
  defaultFilters = {},
  nbItemsByPage = 25,
  page = 1,
  persistKey = null,
} = {}) {
  const memo = readPersistedSearch(persistKey, defaultFilters);
  return {
    searchAtom: atom({
      filters: memo ? memo.filters : { ...defaultFilters },
      page: (memo && memo.page) || page,
      nbItemsByPage: (memo && memo.nbItemsByPage) || nbItemsByPage,
    }),
    resultsAtom: atom({ ...INITIAL_RESULTS }),
  };
}

export const dataServiceFetcher = (endpoint, payload) =>
  new Promise((resolve, reject) => dataService.post(endpoint, payload, resolve, reject));

const latestRequestIds = new WeakMap();
const nextRequestId = (key) => {
  const id = (latestRequestIds.get(key) || 0) + 1;
  latestRequestIds.set(key, id);
  return id;
};
const isLatestRequest = (key, id) => latestRequestIds.get(key) === id;

const identity = (search) => search;

export default function useListSearch({
  searchAtom,
  resultsAtom,
  defaultFilters = {},
  endpoint,
  buildPayload = identity,
  fetcher = dataServiceFetcher,
  autoSearch = true,
  persistKey = null,
}) {
  const [search, setSearch] = useAtom(searchAtom);
  const [results, setResults] = useAtom(resultsAtom);

  const runSearch = useCallback(
    (nextSearch) => {
      const requestId = nextRequestId(resultsAtom);
      if (persistKey) writePersisted(persistKey, nextSearch);
      setResults((r) => ({ ...r, status: "loading", error: null }));
      return fetcher(endpoint, buildPayload(nextSearch)).then(
        (response) => {
          if (!isLatestRequest(resultsAtom, requestId)) return;
          const data = response && response.data;
          setResults({
            items: Array.isArray(data) ? data : [],
            count: Number(response && response.count) || 0,
            status: "success",
            error: null,
            hasSearched: true,
            appliedFilters: nextSearch.filters,
          });
        },
        (err) => {
          if (!isLatestRequest(resultsAtom, requestId)) return;
          setResults((r) => ({
            ...r,
            items: [],
            count: 0,
            status: "error",
            error: (err && err.other) || "Une erreur est survenue",
            hasSearched: true,
            appliedFilters: nextSearch.filters,
          }));
        }
      );
    },
    [resultsAtom, fetcher, endpoint, buildPayload, setResults, persistKey]
  );

  useEffect(() => {
    if (autoSearch && !results.hasSearched && results.status === "idle") {
      runSearch(search);
    }
    // Uniquement au montage : une liste déjà chargée (retour depuis une fiche)
    // conserve ses résultats sans nouvel appel.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const apply = useCallback(
    (nextSearch) => {
      setSearch(nextSearch);
      return runSearch(nextSearch);
    },
    [setSearch, runSearch]
  );

  const setFilter = useCallback(
    (name, value) =>
      setSearch((s) => ({ ...s, page: 1, filters: { ...s.filters, [name]: value } })),
    [setSearch]
  );

  const onFilterChange = useCallback(
    (e) => {
      const { name, value, type, checked } = e.target;
      setFilter(name, type === "checkbox" ? !!checked : value);
    },
    [setFilter]
  );

  const submit = useCallback(() => apply(search), [apply, search]);

  const resetFilters = useCallback(
    () => apply({ ...search, page: 1, filters: { ...defaultFilters } }),
    [apply, search, defaultFilters]
  );

  // `name` : une clé, ou plusieurs (une période porte deux clés).
  const removeFilter = useCallback(
    (name) => {
      const names = Array.isArray(name) ? name : [name];
      const filters = { ...search.filters };
      names.forEach((n) => {
        filters[n] = defaultFilters[n];
      });
      return apply({ ...search, page: 1, filters });
    },
    [apply, search, defaultFilters]
  );

  const changePage = useCallback(
    (page) => apply({ ...search, page: Number(page) || 1 }),
    [apply, search]
  );

  const changeNbItemsByPage = useCallback(
    (nb) =>
      apply({ ...search, page: 1, nbItemsByPage: Number(nb) || search.nbItemsByPage }),
    [apply, search]
  );

  const nbPages = computeNbPages(results.count, search.nbItemsByPage);
  const activeFiltersCount = countActiveFilters(search.filters, defaultFilters);
  const isDirty =
    results.hasSearched &&
    results.appliedFilters !== null &&
    JSON.stringify(search.filters) !== JSON.stringify(results.appliedFilters);

  return {
    search,
    filters: search.filters,
    page: search.page,
    nbItemsByPage: search.nbItemsByPage,
    results,
    items: results.items,
    count: results.count,
    error: results.error,
    hasSearched: results.hasSearched,
    isLoading: results.status === "loading",
    nbPages,
    activeFiltersCount,
    isDirty,
    setSearch,
    setFilter,
    onFilterChange,
    submit,
    resetFilters,
    removeFilter,
    changePage,
    changeNbItemsByPage,
    runSearch,
  };
}
